import React, { useState, useEffect } from 'react';
import { Button, Dialog, Flex, Select, Text, TextField } from '@radix-ui/themes';
import { createPortal } from 'react-dom';
import axiosInstance from '../../config/axiosConfig';
import { toast } from 'react-toastify';
import DialogCloseButton from './DialogCloseButton';

// Define the initial state for the form outside the component
const initialFormData = {
  firstName: '',
  lastName: '',
  userName: '',
  email: '',
  institutionNumber: '',
  supervisor: '',
  dob: '',
  employeeId: '',
  depId: '',
  depLocation: '',
  unitName: '',
  department: '',
};

const CreateUser = ({ initialValues = null, roleId, onUserCreated, onClose }) => {
  const isEditMode = initialValues !== null;

  // Helper function to map initialValues to formData to avoid null/undefined issues
  const getInitialState = () => {
    if (isEditMode && initialValues) {
      return {
        firstName: initialValues.firstName || initialValues.department_name || '',
        lastName: initialValues.lastName || '',
        userName: initialValues.userName || '',
        email: initialValues.email || '',
        institutionNumber: initialValues.institutionNumber || '',
        supervisor: initialValues.supervisor || '',
        dob: initialValues.dob || '',
        employeeId: initialValues.employeeId || '',
        depId: initialValues.depId || initialValues.department_id || '',
        depLocation: initialValues.depLocation || initialValues.department_location || '',
        unitName: initialValues.unitName || initialValues.unit_name || '',
        department: initialValues.department || '',
      };
    }
    return initialFormData;
  };

  const [userType, setUserType] = useState(isEditMode ? initialValues.userType : 'department');
  const [formData, setFormData] = useState(getInitialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      setFormData(getInitialState());
      setUserType(initialValues.userType || 'department');
      setIsOpen(true); // Open modal automatically when editing
    }
  }, [initialValues, isEditMode]);

  // Centralized function to reset state and close the dialog
  const resetFormAndClose = () => {
    setFormData(initialFormData); // Reset form data
    setUserType('department'); // Reset user type dropdown
    setIsOpen(false);
    if (onClose) {
      onClose(); // Notify parent component if needed (e.g., to clear edit state)
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const userData = {
        token: isEditMode ? initialValues.token : null,
        firstName: formData.firstName,
        lastName: formData.lastName,
        userName: formData.userName,
        email: formData.email,
        institutionNumber: formData.institutionNumber,
        role: userType,
        supervisor: formData.supervisor,
        dob: formData.dob || new Date().toISOString(),
        employeeId: formData.employeeId,
        depId: formData.depId,
        depLocation: formData.depLocation,
        unitName: formData.unitName,
      };

      const endpoint = isEditMode ? '/user/update-user' : '/Account/registration';
      const response = await axiosInstance.post(endpoint, userData);

      if (response.data.result) {
        const message = isEditMode ? 'User updated successfully' : 'User created successfully';
        toast.success(message);
        if (onUserCreated) await onUserCreated(); // Refresh the user list
        resetFormAndClose(); // Close and reset form on success
      } else {
        const errorMessage = response.data.errorResponse?.message || 'Failed to process user';
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errorResponse?.message || 'An error occurred while submitting.';
      toast.error(errorMessage);
      console.error('Error submitting user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDepartmentFields = () => (
    <>
      <Flex gap="3">
        <label className="w-[50%]">
          <Text as="div" size="2" mb="1" weight="bold">Department Name</Text>
          <TextField.Root
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            placeholder="Enter department name"
          />
        </label>
        <label className="w-[50%]">
          <Text as="div" size="2" mb="1" weight="bold">Department ID</Text>
          <TextField.Root
            value={formData.depId}
            onChange={(e) => handleInputChange('depId', e.target.value)}
            placeholder="Enter department ID"
          />
        </label>
      </Flex>
      <Flex gap="3">
        <label className="w-full">
          <Text as="div" size="2" mb="1" weight="bold">Department Location</Text>
          <TextField.Root
            value={formData.depLocation}
            onChange={(e) => handleInputChange('depLocation', e.target.value)}
            placeholder="Enter department location"
          />
        </label>
        <label className="w-full">
          <Text as="div" size="2" mb="1" weight="bold">Unit Name</Text>
          <TextField.Root
            value={formData.unitName}
            onChange={(e) => handleInputChange('unitName', e.target.value)}
            placeholder="Enter unit name"
          />
        </label>
      </Flex>
    </>
  );

  const renderCaregiverFields = () => (
    <>
      <Flex gap="3">
        <label className="w-[50%]">
          <Text as="div" size="2" mb="1" weight="bold">First Name</Text>
          <TextField.Root
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            placeholder="Enter first name"
          />
        </label>
        <label className="w-[50%]">
          <Text as="div" size="2" mb="1" weight="bold">Last Name</Text>
          <TextField.Root
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            placeholder="Enter last name"
          />
        </label>
      </Flex>
      <Flex gap="3">
        <label className="w-[50%]">
          <Text as="div" size="2" mb="1" weight="bold">Department</Text>
          <Select.Root
            value={formData.department}
            onValueChange={(value) => handleInputChange('department', value)}
          >
            <Select.Trigger style={{ width: '100%' }} placeholder="Select Department" />
            <Select.Content position="popper">
              <Select.Group>
                <Select.Item value="Department User 1">Department User 1</Select.Item>
                <Select.Item value="Department User 2">Department User 2</Select.Item>
              </Select.Group>
            </Select.Content>
          </Select.Root>
        </label>
        <label className="w-[50%]">
          <Text as="div" size="2" mb="1" weight="bold">Employee ID</Text>
          <TextField.Root
            value={formData.employeeId}
            onChange={(e) => handleInputChange('employeeId', e.target.value)}
            placeholder="Enter employee ID"
          />
        </label>
      </Flex>
    </>
  );

  const dialogContent = isOpen ? (
    <Dialog.Content 
        maxWidth="450px"
        onPointerDownOutside={(e) => e.preventDefault()}
    >
        <DialogCloseButton onClose={resetFormAndClose} />
        <Dialog.Title>{isEditMode ? "Manage User Details" : "Create New User"}</Dialog.Title>
        <Dialog.Description size="2" mb="4">
            {isEditMode ? "Modify the user's details." : "Fill in the details to create a new user."}
        </Dialog.Description>

        <Flex direction="column" gap="3">
            <Flex gap="3">
                <label className="w-[50%]">
                    <Text as="div" size="2" mb="1" weight="bold">User Type</Text>
                    <Select.Root
                        value={userType}
                        onValueChange={(value) => setUserType(value)}
                        disabled={isEditMode}
                    >
                        <Select.Trigger style={{ width: '100%' }} />
                        <Select.Content position="popper">
                            <Select.Group>
                                <Select.Item value="department">Department</Select.Item>
                                {roleId !== '2' && <Select.Item value="care_giver">Caregiver</Select.Item>}
                                <Select.Item value="security">Security</Select.Item>
                                <Select.Item value="housekeeping">Housekeeping</Select.Item>
                                <Select.Item value="f&b">F&B</Select.Item>
                            </Select.Group>
                        </Select.Content>
                    </Select.Root>
                </label>
                <label className="w-[50%]">
                    <Text as="div" size="2" mb="1" weight="bold">Username</Text>
                    <TextField.Root
                        value={formData.userName}
                        onChange={(e) => handleInputChange('userName', e.target.value)}
                        placeholder="Enter username"
                        disabled={isEditMode}
                    />
                </label>
            </Flex>
            <Flex gap="3">
                <label className="w-full">
                    <Text as="div" size="2" mb="1" weight="bold">Email</Text>
                    <TextField.Root
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter email"
                        disabled={isEditMode}
                    />
                </label>
            </Flex>
            {userType === 'department' && renderDepartmentFields()}
            {userType === 'care_giver' && renderCaregiverFields()}
        </Flex>

        <Flex gap="3" mt="4" justify="end">
            <Button
                variant="soft"
                color="gray"
                onClick={resetFormAndClose}
                disabled={isSubmitting}
            >
                Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isEditMode ? "Update" : "Save"}
            </Button>
        </Flex>
    </Dialog.Content>
  ) : null;

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          resetFormAndClose();
        } else {
          setIsOpen(true);
        }
      }}
    >
      {!isEditMode && (
        <Dialog.Trigger>
          <Button onClick={() => setIsOpen(true)}>Create New User</Button>
        </Dialog.Trigger>
      )}
      {isOpen && createPortal(dialogContent, document.body)}
    </Dialog.Root>
  );
};

export default CreateUser;
